type EasingFunction = (t: number) => number;

interface CascadingCounterOptions {
	selector: string;
	threshold?: number;
	triggerWindowMs?: number;
	cascadeDelayMs?: number;
}
interface CounterOptions {
	from?: number;
	to: number;
	duration: number; // ms
	easing?: EasingFunction;
	onUpdate: (value: number) => void;
	onComplete?: () => void;
}

export class NumberInitializer {
	/**
	 * Initializes the Number Module.
	 *
	 * @returns void
	 */
	init() {
		new CascadingViewportCounters({
			selector: '.number-module-container .number-container .number',
			threshold: 0.4,
			triggerWindowMs: 150,
			cascadeDelayMs: 450,
		});
	}
}

export class CascadingViewportCounters {
	private lastTriggerTime = 0;
	private offsetCounter = 0;
	private observer!: IntersectionObserver;

	constructor(options: CascadingCounterOptions) {
		const { selector, threshold = 0.4, triggerWindowMs = 150, cascadeDelayMs = 450 } = options;

		const elements = document.querySelectorAll<HTMLElement>(selector);
		if (!elements.length) return;

		this.observer = new IntersectionObserver(
			(entries) => {
				entries
					.filter((entry) => entry.isIntersecting)
					.forEach((entry) =>
						this.handleIntersect(entry.target as HTMLElement, triggerWindowMs, cascadeDelayMs)
					);
			},
			{ threshold }
		);

		elements.forEach((el) => this.observer.observe(el));
	}

	private handleIntersect(element: HTMLElement, triggerWindowMs: number, cascadeDelayMs: number): void {
		const now = performance.now();

		if (now - this.lastTriggerTime < triggerWindowMs) {
			this.offsetCounter++;
		} else {
			this.offsetCounter = 0;
		}

		const delay = this.offsetCounter * cascadeDelayMs;

		setTimeout(() => {
			this.startCounter(element);
			this.observer.unobserve(element); // run only once
		}, delay);

		this.lastTriggerTime = now;
	}

	private startCounter(element: HTMLElement): void {
		const target = parseInt(element.dataset.count ?? '0', 10);

		const counter = new CountUpAnimator({
			to: target,
			duration: Math.min(500 + target * 1.5, 3000),
			onUpdate: (value) => {
				element.textContent = value.toString();
			},
		});

		counter.start();
	}
}

export class CountUpAnimator {
	private startTime: number | null = null;
	private duration: number;
	private readonly from: number;
	private readonly to: number;
	private readonly easing: EasingFunction;
	private readonly onUpdate: (value: number) => void;
	private readonly onComplete?: () => void;

	constructor(options: CounterOptions) {
		this.from = options.from ?? 0;
		this.to = options.to;
		this.duration = options.duration;
		this.onUpdate = options.onUpdate;
		this.onComplete = options.onComplete;

		this.easing = options.easing ?? CountUpAnimator.easeInOutCubic;
	}

	public start(): void {
		requestAnimationFrame(this.animate);
	}

	private animate = (timestamp: number): void => {
		if (this.startTime === null) {
			this.startTime = timestamp;
		}

		const elapsed = timestamp - this.startTime;
		const progress = Math.min(elapsed / this.duration, 1);
		const easedProgress = this.easing(progress);

		const currentValue = this.from + (this.to - this.from) * easedProgress;

		this.onUpdate(Math.round(currentValue));

		if (progress < 1) {
			requestAnimationFrame(this.animate);
		} else {
			this.onUpdate(this.to);
			this.onComplete?.();
		}
	};

	/**
	 * Ease-in-out cubic
	 */
	public static easeInOutCubic(t: number): number {
		return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
	}
}
